import type { JSX } from 'react';
import { useFailureOutlasted } from '../hooks/useFailureOutlasted';
import { onNavClick, useRouteHref } from '../lib/router';
import { refreshExecutiveView, useExecutiveView } from '../lib/use-executive-view';
import '../styles/owner-waiting.css';

export const OWNER_WAITING_HREF = '/hoje?aguardando=1';
export const OWNER_WAITING_FOCUS_EVENT = 'td-owner-waiting-focus';
function focusWaiting(): void {
  window.dispatchEvent(new Event(OWNER_WAITING_FOCUS_EVENT));
  void refreshExecutiveView();
}

/**
 * Always visible, including mobile: no menu or project filter hides the owner's pending acts. In the Android app the bar
 * moves into Gestão (`placement="peek"`) and the count rides on the Gestão button (owner, 14/10: the fixed bar ate screen).
 */
export function OwnerWaitingAccess({ placement = 'bar' }: { placement?: 'bar' | 'peek' } = {}): JSX.Element {
  const { view, error, errorSinceMs } = useExecutiveView();
  // 796fa1a4: na conversa do dono e no Gestão dele, falha de leitura é aviso técnico; a passageira não aparece e a que
  // passa de 1 min vira só "pode estar desatualizado". As telas de operação (/deck, /product…) mantêm o estado técnico.
  const route = useRouteHref();
  const ownerSurface = placement === 'peek' || route.split('?')[0] === '/donizeti';
  const outlasted = useFailureOutlasted(error ? errorSinceMs : null);
  const failed = ownerSurface ? outlasted : !!error;
  const count = view?.waitingOnOwner.length;
  // Sem nenhuma leitura, "conferindo pendências…" depois de 1 min de falha fingia que ainda carregava.
  const unread = ownerSurface && failed && !view;
  const note = ownerSurface ? (unread ? 'pendências indisponíveis agora' : failed ? 'pode estar desatualizado' : count === undefined ? 'conferindo pendências…' : count ? 'ver pendências →' : 'nada agora')
    : error ? `${view ? 'última leitura · ' : ''}leitura indisponível · tentar novamente` : count === undefined ? 'conferindo pendências…' : count ? 'ver pendências →' : 'nada agora';
  return <a className={`owner-waiting-access${placement === 'peek' ? ' owner-waiting-access--peek' : ''}${count ? ' has-waiting' : ''}${ownerSurface && failed ? ' is-stale' : ''}`} href={OWNER_WAITING_HREF}
    onClick={onNavClick(OWNER_WAITING_HREF, focusWaiting)} data-testid="owner-waiting-access">
    <span>Aguardando você</span>
    <strong title={failed && view ? ownerSurface ? 'Contagem da última leitura; atualiza sozinha' : 'Contagem da última leitura; estado atual indisponível' : unread ? 'Atualiza sozinha quando a leitura voltar' : undefined}>{count === undefined ? unread ? '—' : '…' : count}</strong>
    <span>{note}</span>
  </a>;
}

/** Executive waiting items were absent from Atenção's workboard. Preserve their canonical source. */
export function OwnerWaitingSummary(): JSX.Element {
  const { view, error } = useExecutiveView();
  return <section className="owner-waiting-summary" aria-labelledby="owner-waiting-title" data-testid="owner-waiting-summary">
    <h2 id="owner-waiting-title">Aguardando você <span>{view?.waitingOnOwner.length ?? '…'}</span></h2>
    {error ? <p role="status">Não foi possível atualizar as pendências. {view ? 'A lista e a contagem são da última leitura. ' : ''}<button onClick={() => { void refreshExecutiveView(); }}>Tentar novamente</button></p> : null}
    {view ? view.waitingOnOwner.length ? <ul>{view.waitingOnOwner.map((item, i) => <li key={item.id ?? `${item.source}-${item.n ?? i}`}>
      <span>{item.text}</span>
      <a href={OWNER_WAITING_HREF} onClick={onNavClick(OWNER_WAITING_HREF, focusWaiting)}>ver como fazer</a>
    </li>)}</ul> : <p>{error ? 'Na última leitura não havia pendências; o estado atual não pôde ser conferido.' : 'Nada depende de você agora.'}</p> : error ? null : <p role="status">Conferindo pendências…</p>}
  </section>;
}
